/**
 * @fileoverview screen-protection.constants.ts
 * @description
 * **The Screen Guardian Constants**
 * Centralized configuration and content for the screen protection feature,
 * including detection thresholds, timing values, assets, messages, and
 * animation presets used by the black screen overlay.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Single source of truth for screen-protection timings and assets
 * 2. Declarative animation configuration
 * 3. Clear separation of content from behavior
 */

export const SCREEN_PROTECTION_CONSTANTS = {
  DEVTOOLS_DETECTION_THRESHOLD: 160,
  DEVTOOLS_CHECK_INTERVAL_MS: 500,
  COUNTDOWN_INITIAL_VALUE: 3,
  COUNTDOWN_INTERVAL_MS: 1000,
  PRINT_DIALOG_TIMEOUT_MS: 1000,
  PRINT_KEYBOARD_TIMEOUT_MS: 2000,
  VISIBILITY_CHECK_DELAY_MS: 100,
  DISSOLVE_ANIMATION_DELAY_MS: 1500,
  DISSOLVE_ANIMATION_DURATION_MS: 1000,
  SELF_PROTECTION_REATTACH_DELAY_MS: 10,
} as const;

export const SCREEN_PROTECTION_ASSETS = {
  AWAY_IMAGE: 'assets/images/page/away.png',
  RESTRICTED_IMAGE: 'assets/images/page/restricted.png',
} as const;

export const SCREEN_PROTECTION_MESSAGES = {
  AWAY_TITLE: 'You Are Away',
  AWAY_SUBTITLE: 'Please return to continue',
  WELCOME_TITLE: 'Welcome Back',
  WELCOME_SUBTITLE: 'Resuming your session',
  DEVTOOLS_MESSAGE:
    'This content cannot be displayed while developer tools are open.',
  DEVTOOLS_HELP: 'Please close developer tools to continue.',
  PRINT_MESSAGE: 'This content cannot be displayed during print attempts.',
  PRINT_HELP: 'Please cancel the print operation to continue.',
} as const;

/**
 * Animation configuration for the black screen overlay.
 * Values are tuned for a smooth but responsive experience and are expressed
 * in seconds or pixels where appropriate.
 */
export const SCREEN_PROTECTION_ANIMATION_CONSTANTS = {
  // Global trigger
  ANIMATION_TRIGGER_DELAY: 0.05,

  // Countdown
  COUNTDOWN_INITIAL_SCALE: 0.4,
  COUNTDOWN_FINAL_SCALE: 1.1,
  COUNTDOWN_NORMAL_SCALE: 1,
  COUNTDOWN_PULSE_SCALE: 1.15,
  COUNTDOWN_INITIAL_ROTATION: -10,
  COUNTDOWN_INITIAL_Y: 30,
  COUNTDOWN_BLUR: 'blur(12px)',
  COUNTDOWN_DURATION: 0.6,
  COUNTDOWN_PULSE_DURATION: 0.25,
  COUNTDOWN_PULSE_DELAY: 0.35,

  // Away image
  AWAY_IMAGE_INITIAL_SCALE: 0.7,
  AWAY_IMAGE_FINAL_SCALE: 1,
  AWAY_IMAGE_INITIAL_Y: 40,
  AWAY_IMAGE_INITIAL_ROTATION: -8,
  AWAY_IMAGE_BLUR: 'blur(14px)',
  AWAY_IMAGE_DURATION: 0.9,
  AWAY_IMAGE_FLOAT_Y: 10,
  AWAY_IMAGE_FLOAT_DURATION: 2.4,
  AWAY_IMAGE_FLOAT_DELAY: 0.8,

  // Away subtitle
  AWAY_SUBTITLE_INITIAL_Y: 20,
  AWAY_SUBTITLE_INITIAL_SCALE: 0.95,
  AWAY_SUBTITLE_BLUR: 'blur(8px)',
  AWAY_SUBTITLE_DURATION: 0.6,
  AWAY_SUBTITLE_OFFSET: 0.25,

  // Away title characters
  AWAY_TITLE_CHAR_INITIAL_Y: 18,
  AWAY_TITLE_CHAR_DURATION: 0.7,
  AWAY_TITLE_CHAR_STAGGER: 0.03,

  // Away dots
  AWAY_DOT_INITIAL_SCALE: 0.2,
  AWAY_DOT_INITIAL_Y: 16,
  AWAY_DOT_INITIAL_ROTATION: -10,
  AWAY_DOT_OPACITY: 0.8,
  AWAY_DOT_DURATION: 0.5,
  AWAY_DOT_STAGGER: 0.1,
  AWAY_DOT_OFFSET: 0.4,
  AWAY_DOT_BOUNCE_Y: -6,
  AWAY_DOT_BOUNCE_SCALE: 1.15,
  AWAY_DOT_BOUNCE_DURATION: 0.8,
  AWAY_DOT_BOUNCE_DELAY_INCREMENT: 0.15,
  AWAY_DOT_BOUNCE_DELAY_BASE: 0.4,

  // Welcome title words
  WELCOME_WORD_INITIAL_Y: 24,
  WELCOME_WORD_INITIAL_SCALE: 0.9,
  WELCOME_WORD_DURATION: 0.6,
  WELCOME_WORD_STAGGER: 0.08,
  WELCOME_WORD_BOUNCE_Y: -10,
  WELCOME_WORD_BOUNCE_DURATION: 0.6,
  WELCOME_WORD_BOUNCE_STAGGER: 0.05,
  WELCOME_WORD_BOUNCE_OFFSET: 0.4,

  // Welcome subtitle characters
  WELCOME_CHAR_DURATION: 0.4,
  WELCOME_CHAR_STAGGER: 0.015,
  WELCOME_CHAR_OFFSET: 0.3,

  // Restricted help text
  RESTRICTED_HELP_INITIAL_Y: 16,
  RESTRICTED_HELP_INITIAL_SCALE: 0.95,
  RESTRICTED_HELP_BLUR: 'blur(10px)',
  RESTRICTED_HELP_DURATION: 0.6,
  RESTRICTED_HELP_OFFSET: 0.3,

  // Restricted image
  RESTRICTED_IMAGE_INITIAL_SCALE: 0.8,
  RESTRICTED_IMAGE_FINAL_SCALE: 1,
  RESTRICTED_IMAGE_INITIAL_ROTATION: -6,
  RESTRICTED_IMAGE_BLUR: 'blur(12px)',
  RESTRICTED_IMAGE_DURATION: 0.8,
  RESTRICTED_IMAGE_SHAKE_X: 10,
  RESTRICTED_IMAGE_SHAKE_DURATION: 0.3,
  RESTRICTED_IMAGE_SHAKE_REPEAT: 3,
  RESTRICTED_IMAGE_SHAKE_OFFSET: 0.2,
  RESTRICTED_IMAGE_PULSE_SCALE: 1.03,
  RESTRICTED_IMAGE_PULSE_DURATION: 1.4,
  RESTRICTED_IMAGE_PULSE_DELAY: 1.2,

  // Restricted message
  RESTRICTED_MESSAGE_INITIAL_X: -24,
  RESTRICTED_MESSAGE_INITIAL_SCALE: 0.95,
  RESTRICTED_MESSAGE_BLUR: 'blur(10px)',
  RESTRICTED_MESSAGE_DURATION: 0.7,
  RESTRICTED_MESSAGE_OFFSET: 0.25,
  RESTRICTED_MESSAGE_SHAKE_X: 6,
  RESTRICTED_MESSAGE_SHAKE_DURATION: 0.25,
  RESTRICTED_MESSAGE_SHAKE_REPEAT: 3,
  RESTRICTED_MESSAGE_SHAKE_OFFSET: 0.6,

  // Dissolve animation
  DISSOLVE_CONTENT_SCALE: 0.95,
  DISSOLVE_CONTENT_ROTATION: 3,
  DISSOLVE_CONTENT_BLUR: 'blur(10px)',
  DISSOLVE_CONTENT_DURATION: 0.5,
  DISSOLVE_OVERLAY_SCALE: 1.05,
  DISSOLVE_OVERLAY_BLUR: 'blur(14px)',
  DISSOLVE_OVERLAY_DURATION: 0.6,
  DISSOLVE_OVERLAY_OFFSET: 0.2,
} as const;
